import { useEffect, useState } from "react";
import { parseUnits, formatUnits } from "ethers";
import { ArrowDown } from "lucide-react";
import { buildPath, quoteSwap, dexSwap } from "@/lib/dex";
import { AmountBox, ActionBtn, fmtAmt } from "@/components/dex/DexParts";

const toRaw = (v, d) => { try { return v && Number(v) > 0 ? parseUnits(String(v), d) : 0n; } catch { return 0n; } };

export const DexSwapPanel = ({ dex, address, run, busy, step }) => {
  const { tokens, pairs, router, wfmi } = dex;
  const [tokenIn, setTokenIn] = useState(null);
  const [tokenOut, setTokenOut] = useState(null);
  const [amount, setAmount] = useState("");
  const [out, setOut] = useState(null);
  const [error, setError] = useState("");
  const amountIn = tokenIn ? toRaw(amount, tokenIn.decimals) : 0n;
  const path = tokenIn && tokenOut && tokenIn.address !== tokenOut.address ? buildPath(pairs, tokenIn, tokenOut, wfmi) : null;
  const pathKey = path?.join(",");

  useEffect(() => {
    setOut(null);
    setError("");
    if (!path || !amountIn || !router) return undefined;
    const id = setTimeout(() => quoteSwap(router, amountIn, path).then(setOut).catch(() => setError("Not enough liquidity for this amount")), 400);
    return () => clearTimeout(id);
  }, [pathKey, amountIn, router]); // eslint-disable-line react-hooks/exhaustive-deps

  const noRoute = tokenIn && tokenOut && tokenIn.address !== tokenOut.address && !path;
  const onSwap = () => run((onStep) => dexSwap({ eip1193: address.eip1193, routerAddr: router, path, tokenIn, tokenOut, amountIn, expectedOut: out, onStep })
    .then((h) => { setAmount(""); return h; }), `Swapped ${amount} ${tokenIn.symbol} for ${tokenOut.symbol}`);

  return (
    <div data-testid="dex-swap-panel" className="space-y-3">
      <AmountBox label="You pay" amount={amount} onAmount={setAmount} token={tokenIn} tokens={tokens} onToken={setTokenIn} testId="dex-swap-in" />
      <div className="flex justify-center"><ArrowDown size={16} className="text-[#D4AF37]" /></div>
      <AmountBox label="You receive (estimated)" amount={out != null && tokenOut ? formatUnits(out, tokenOut.decimals) : ""} token={tokenOut} tokens={tokens} onToken={setTokenOut} testId="dex-swap-out" readOnly />
      {path && <p data-testid="dex-swap-route" className="text-xs text-zinc-500">Route: {path.length === 2 ? "direct pair" : "via FMI"} · 0.2% fee per hop · 1% max slippage</p>}
      {out != null && tokenOut && <p data-testid="dex-swap-min" className="text-xs text-zinc-500">Minimum received: {fmtAmt(Number(formatUnits(out, tokenOut.decimals)) * 0.99)} {tokenOut.symbol}</p>}
      {noRoute && <p data-testid="dex-swap-noroute" className="text-xs text-amber-300">No pair for these tokens yet. Create one in the Add liquidity tab.</p>}
      {error && <p data-testid="dex-swap-error" className="text-xs text-red-400">{error}</p>}
      <ActionBtn testId="dex-swap-submit" busy={busy} step={step} disabled={!address.account || !out} onClick={onSwap}>
        {address.account ? "Swap" : "Connect wallet to swap"}
      </ActionBtn>
    </div>
  );
};
